import { customOrderColumnPfx } from '~/constants';
import type {
    VDataTableHeaderCustom
} from 'x1-base/types';
import {
    baseHeadersRawFillIn,
    headersStartIdxFillIn,
    baseHeadersRawBooking,
    headersStartIdxBooking,
    baseSelectedHeadersFillIn,
    baseSelectedHeadersBooking
} from '~/models';


interface SkusTableState {
    customDateHeaders: VDataTableHeaderCustom[];
    baseHeaders: Ref<VDataTableHeaderCustom[]>;
    customHeadersStartIndex: Ref<number>;
    selectedBaseHeaderNames: Ref<string[]>;
}

// Table state is created per Nuxt app instance: per request on the server,
// once per session on the client. At module scope it would be shared by every
// SSR request for the lifetime of the server process.
function useSkusTableState(): SkusTableState {
    const nuxtApp = useNuxtApp() as unknown as { _skusTableState?: SkusTableState };

    nuxtApp._skusTableState ??= {
        customDateHeaders: reactive<VDataTableHeaderCustom[]>([]),
        baseHeaders: ref<VDataTableHeaderCustom[]>(baseHeadersRawFillIn),
        customHeadersStartIndex: ref(headersStartIdxFillIn),
        selectedBaseHeaderNames: ref<string[]>(baseSelectedHeadersFillIn)
    };

    return nuxtApp._skusTableState;
}

export function useSkusTable() {
    const {
        customDateHeaders,
        baseHeaders,
        customHeadersStartIndex,
        selectedBaseHeaderNames
    } = useSkusTableState();

    const columnsOrderDateKeys = computed<string[]>(() => customDateHeaders.map((h: VDataTableHeaderCustom) => h.key));
    const sortedCustomDateHeaders = computed(() => customDateHeaders.sort((a: VDataTableHeaderCustom, b: VDataTableHeaderCustom) => Date.parse(a.value) - Date.parse(b.value)));

    const headers = computed(() => {
        const h = baseHeaders.value.slice(0);
        h.splice(customHeadersStartIndex.value, 0, ...sortedCustomDateHeaders.value);
        return h.filter((h: VDataTableHeaderCustom) => selectedBaseHeaderNames.value.includes(h.key) || (h.isCustom && !h.isHidden));
    });

    function addCustomHeader(key: string, props: object): void {
        if (isColumnExists(key)) {
            console.info('Column already exists: ', key);
            return;
        }

        customDateHeaders.push({
            key,
            sortable: false,
            ...props
        });
    }

    function updateCustomHeader(key: string, props: object): void {
        const header = customDateHeaders.find((h: VDataTableHeaderCustom) => {
            return h.key === getColumnKey(key);
        });

        if (!header) {
            console.info('Cannot update: Column not found: ', key);
            return;
        }

        Object.assign(header, props);
    }

    function toggleCustomHeaderVisibility(key: string, show: boolean): void {
        updateCustomHeader(key, { isHidden: !show });
    }

    function deleteCustomHeader(colKey: string): void {
        const headerIdx = customDateHeaders.findIndex((h: VDataTableHeaderCustom) => h.key === colKey);

        if (headerIdx === -1) {
            console.info('Cannot delete: column not found: ', colKey);
            return;
        }

        customDateHeaders.splice(headerIdx, 1);
    }

    function toggleBookingColumns(isBooking?: boolean): void {
        baseHeaders.value = isBooking ? baseHeadersRawBooking : baseHeadersRawFillIn;
        selectedBaseHeaderNames.value = isBooking ? baseSelectedHeadersBooking : baseSelectedHeadersFillIn;

        nextTick(() => {
            customHeadersStartIndex.value = isBooking ? headersStartIdxBooking : headersStartIdxFillIn;
        });
    }

    function destroy(): void {
        customDateHeaders.splice(0);
    }

    // Helpers

    function getColumnKey(id: string): string {
        if (id.startsWith(customOrderColumnPfx)) {
            return id;
        }

        return `${customOrderColumnPfx}${id}`;
    }

    function isColumnExists(id: string): boolean {
        return !!customDateHeaders.find((h: VDataTableHeaderCustom) => h.key === getColumnKey(id));
    }

    return {
        customOrderColumnPfx,
        baseHeaders,
        customDateHeaders,
        customHeadersStartIndex,
        selectedBaseHeaderNames,
        columnsOrderDateKeys,
        headers,
        sortedCustomDateHeaders,
        addCustomHeader,
        updateCustomHeader,
        toggleCustomHeaderVisibility,
        toggleBookingColumns,
        deleteCustomHeader,
        getColumnKey,
        destroy
    };
}
